import _ from 'lodash';
import styled from 'styled-components';
import { asCurrency, asCurrencyWithDigits } from '../utils/formatters';
import EditCropLink from '../components/EditCropLink';
import { getOperationEquipmentType } from '../utils/operations';
import { ASSIGNMENTS, formatOperationGroup } from './operations';
import { UnstyledButton } from '../components/basics/UnstyledButton';
import ChevronIcon from '../components/icons/ChevronIcon';

const returnZeroIfDeveloper = (operation, val) =>
  operation.assignment === ASSIGNMENTS.OWNER ? val : 0;

const greyStyle = {
  backgroundColor: 'rgba(33, 33, 33, 0.05)',
};

const getConditionalDisplayStyle = (shown) =>
  shown ? {} : { display: 'none' };

const ToggleButton = styled(UnstyledButton)`
  color: var(--text-muted-color);
  &:hover {
    color: var(--text-color);
  }
`;

/* A header with a toggle for the cost breakdown columns */
const BaseCostToggleHeader = ({ isShown, toggleIsShown }) => (
  <div className="d-flex">
    Base Cost
    <ToggleButton
      className="ml-2"
      title={`${isShown ? 'Hide' : 'Show'} breakdown`}
    >
      <ChevronIcon
        direction={isShown ? 'left' : 'right'}
        onClick={toggleIsShown}
      />
    </ToggleButton>
  </div>
);

export const getOperationsMetrics = (showLink) => (category) => {
  return [
    {
      displayString: 'Activity',
      accessor: 'name',
      format: (name, op) =>
        showLink ? (
          <EditCropLink operation={op} crop={op.crop}>
            {name}
          </EditCropLink>
        ) : (
          name
        ),
    },
    {
      displayString: 'Total Cost',
      accessor: 'totalCostByYear',
      showGroupTotal: true,
      format: asCurrency,
    },
    {
      displayString: 'Labor',
      accessor: 'laborCostByYear',
      format: (val, item) => asCurrency(returnZeroIfDeveloper(item, val)),
    },
    {
      displayString: 'Input',
      accessor: 'inputCostByYear',
      format: (val, item) => asCurrency(returnZeroIfDeveloper(item, val)),
    },
    {
      displayString: 'Fuel',
      accessor: 'fuelCostByYear',
      format: (val, item) => asCurrency(returnZeroIfDeveloper(item, val)),
    },
    {
      displayString: 'Contract/Rent',
      accessor: 'contractOrRentCostByYear',
      format: (val, item) => asCurrency(returnZeroIfDeveloper(item, val)),
    },
    {
      displayString: 'Depreciation',
      accessor: 'depreciationCostByYear',
      format: (val, item) => asCurrency(returnZeroIfDeveloper(item, val)),
    },
    {
      displayString: 'Developer',
      accessor: 'developerCostByYear',
      format: asCurrency,
    },
    {
      displayString: 'Times per year',
      accessor: 'timesPerYear',
    },
    // Hide columns for marketing
    ...(category === 'marketing'
      ? []
      : [
          {
            displayString: 'Equipment',
            accessor: 'equipmentDisplayName',
          },
          {
            displayString: 'Inputs',
            accessor: 'inputsDisplayName',
          },
        ]),
    {
      displayString: 'Assignment',
      accessor: 'assignment',
      format: _.capitalize,
      exportOnly: true,
    },
    {
      displayString: 'Operation Type',
      accessor: 'contracted',
      format: (contracted, op) => getOperationEquipmentType(op),
    },
    ...(category === null
      ? [
          {
            displayString: 'Category',
            accessor: 'category',
            format: _.capitalize,
          },
        ]
      : []),
    {
      displayString: 'Group',
      accessor: 'group',
      format: formatOperationGroup,
    },
  ];
};

export const getMultiYearOperationsMetrics = (showLink, showSubsidy) => (
  category,
) => {
  return [
    {
      displayString: 'Activity',
      accessor: 'name',
      format: (name, op) =>
        showLink ? (
          <EditCropLink operation={op} crop={op.crop}>
            {name}
          </EditCropLink>
        ) : (
          name
        ),
    },
    // Hide some columns for installation
    // And show others
    ...(category === 'installation'
      ? [
          {
            displayString: 'Total Cost',
            accessor: 'totalCost',
            showGroupTotal: true,
            format: asCurrency,
          },
        ]
      : [
          {
            displayString: 'Max Cost',
            accessor: 'maxCost',
            format: asCurrency,
          },
          {
            displayString: 'Avg. Cost',
            accessor: 'avgCost',
            format: asCurrency,
          },
        ]),
    ...(showSubsidy
      ? [
          {
            displayString:
              category === 'installation'
                ? 'Subsidy/stem'
                : 'Avg. Subsidy/stem (30 Yr)',
            accessor: (item) =>
              item.unsubsidizedTotalCostPerTree - item.totalCostPerTree,
            format: asCurrencyWithDigits(2, 4),
            hideIfAllMatchFn: (value) => value === 0,
          },
        ]
      : []),
    // Hide columns for marketing
    ...(category === 'marketing'
      ? []
      : [
          {
            displayString: 'Equipment',
            accessor: 'equipmentDisplayName',
          },
          {
            displayString: 'Inputs',
            accessor: 'inputsDisplayName',
          },
        ]),
    {
      displayString: 'Assignment',
      accessor: 'assignment',
      format: _.capitalize,
      exportOnly: true,
    },
    {
      displayString: 'Operation Type',
      accessor: 'contracted',
      format: (contracted, op) => getOperationEquipmentType(op),
    },
    ...(category === null
      ? [
          {
            displayString: 'Category',
            accessor: 'category',
            format: _.capitalize,
          },
        ]
      : []),
    {
      displayString: 'Group',
      accessor: 'group',
      format: formatOperationGroup,
    },
  ];
};

export const getOperationsBasisMetrics = (
  category,
  showCostBreakdown,
  toggleCostBreakdown,
) => {
  const breakdownDisplayStyle = getConditionalDisplayStyle(showCostBreakdown);
  const conditionalBreakdownStyles = {
    style: { ...greyStyle, ...breakdownDisplayStyle },
    thStyle: breakdownDisplayStyle,
  };

  return [
    {
      displayString: 'Activity',
      accessor: 'name',
      format: (name, op) => (
        <EditCropLink operation={op} crop={op.crop}>
          {name}
        </EditCropLink>
      ),
    },
    {
      displayString: (
        <BaseCostToggleHeader
          isShown={showCostBreakdown}
          toggleIsShown={toggleCostBreakdown}
        />
      ),
      showGroupTotal: true,
      exportDisplayString: 'Base Cost',
      accessor: 'totalCostByYear',
      format: asCurrency,
    },
    {
      displayString: 'Labor',
      accessor: 'laborCostByYear',
      format: asCurrency,
      ...conditionalBreakdownStyles,
    },
    {
      displayString: 'Input',
      accessor: 'inputCostByYear',
      format: asCurrency,
      ...conditionalBreakdownStyles,
    },
    {
      displayString: 'Fuel',
      accessor: 'fuelCostByYear',
      format: asCurrency,
      ...conditionalBreakdownStyles,
    },
    {
      displayString: 'Sub-Contracted',
      accessor: 'contractCostByYear',
      format: asCurrency,
      ...conditionalBreakdownStyles,
    },
    {
      displayString: 'Rental',
      accessor: 'rentalCostByYear',
      format: asCurrency,
      ...conditionalBreakdownStyles,
    },
    {
      displayString: 'Depreciation',
      accessor: 'depreciationCostByYear',
      format: asCurrency,
      ...conditionalBreakdownStyles,
    },
    {
      showGroupTotal: true,
      displayString: 'Buffered Cost',
      accessor: 'bufferedTotalCostByYear',
      format: asCurrency,
    },
    {
      showGroupTotal: true,
      displayString: 'Quoted Cost',
      accessor: 'quotedTotalCostByYear',
      format: asCurrency,
    },
    {
      displayString: 'Times per year',
      accessor: 'timesPerYear',
    },
    // Hide columns for marketing
    ...(category === 'marketing'
      ? []
      : [
          {
            displayString: 'Equipment',
            accessor: 'equipmentDisplayName',
          },
          {
            displayString: 'Inputs',
            accessor: 'inputsDisplayName',
          },
        ]),
    {
      displayString: 'Operation Type',
      accessor: 'contracted',
      format: (contracted, op) => getOperationEquipmentType(op),
    },
    ...(category === null
      ? [
          {
            displayString: 'Category',
            accessor: 'category',
            format: _.capitalize,
          },
        ]
      : []),
    {
      displayString: 'Group',
      accessor: 'group',
      format: formatOperationGroup,
    },
  ];
};
